// HU-27 · HU-28 · El cierre del caso y su reapertura, como TARJETA DEL EXPEDIENTE.
//
// POR QUE EXISTE ESTE ARCHIVO (bug 702, CP-E2E-03 paso 4)
// -------------------------------------------------------
// La tarjeta vivía DENTRO de `TramiteDelCaso`, en la bandeja regulatoria. Y un caso NO
// GRAVE no tiene trámite regulatorio —RN-15—, así que para él la tarjeta no existía en
// ninguna pantalla. Vive aquí, en el expediente, que todo caso enviado tiene.
//
// QUÉ CAMBIÓ EN EL SPRINT 3 (`ui/MG-ILE-54.md` «Cierre del caso», `ui/MG-ILE-56.md`)
// ---------------------------------------------------------------------------------
// · TODAS las condiciones, cumplidas y pendientes, con el texto del servidor (HU-27-C14,
//   `condiciones[]` del AT S3 §2.2.5): C-05, C-06 y C-07 (graves), C-08 y RN-43. La pantalla
//   no decide cuáles tocan: el servidor solo manda las que aplican a la gravedad (C8).
// · El «cerrado por» ya NO viaja en el cuerpo: sale de la sesión (HU-27-C7, AT S3 §2.0).
// · «Reabrir» con motivo y reclasificación de la gravedad (HU-28), solo para el Comité.
//
// QUIÉN LA VE. Consultan el cierre el Comité, Asuntos Regulatorios y el Representante del País
// (`AMBITO_DEL_PROCESO` del backend). «Reabrir» sale de `caso.acciones_permitidas` del expediente
// (`reabrir`: el Comité y un caso cerrado, HU-28-C1 y C5), y el modal ofrece las gravedades de
// `caso.reclasificaciones_permitidas` con el aviso `caso.texto_del_plazo_si_grave` ANTES de
// confirmar (HU-28-C7 a C9). Lo que SIGUE por rol, porque el contrato no trae su campo (vacío
// anotado: no existe `cierre.acciones_permitidas`): ver la tarjeta y «Cerrar caso». El servidor
// decide de verdad (403).
//
// EL ESTADO DEL CASO YA NO SE PINTA AQUÍ. «Cerrado», «Reabierto 1 vez» y «cerrado por» son de la
// cabecera del expediente (`caso.*`, `ui/MG-ILE-54.md` «Cabecera»), que todos los roles ven; esta
// tarjeta solo la ven tres. Tras cerrar o reabrir, `onCambio` refresca el expediente (§8.1 R3).
//
// Y SE CALLA ANTE UN 404 o un 403. Un caso que el contexto regulatorio no conoce o que no es de
// su ámbito no tiene cierre del que hablar en esta pantalla.

import { useCallback, useEffect, useId, useState } from 'react';
import { AlertTriangle, CheckCircle2, ListChecks, Lock, RotateCcw } from 'lucide-react';
import { Alert, Button, Card, Checkbox, Field, Modal, Radio, Textarea } from './ui';
import { cerrarCaso, estadoDelCierre, reabrirCaso } from '../api/regulatorio';
import type { CierreBloqueado, CondicionDelCierre, EstadoDelCierre } from '../api/regulatorio';
import { ACCION_REABRIR } from '../api/expedientes';
import type { CasoDelExpediente } from '../api/expedientes';
import { aFallo } from '../api/cliente';
import type { Fallo } from '../api/cliente';
import { tieneRol, usuarioActual } from '../lib/sesion';
import { GRAVEDADES } from '../lib/etiquetas';

/** Quién consulta el cierre (espejo de `AMBITO_DEL_PROCESO`). */
const ROLES_QUE_VEN_EL_CIERRE = ['comite', 'asuntos_regulatorios', 'representante_pais'];
/**
 * Quién cierra (espejo de `SOLO_QUIEN_CIERRA`, RNF-01). Sigue por rol: el contrato no trae
 * `cierre.acciones_permitidas` (vacío anotado). Lo usa también «Cerrar el caso» por fila de
 * Disposiciones y seguimiento, que lleva a esta tarjeta.
 */
export const ROLES_QUE_CIERRAN = ['comite', 'asuntos_regulatorios'];

/** HU-28-C2, literal. */
const MOTIVO_OBLIGATORIO = 'Escribe el motivo: sin motivo no se reabre';
/** HU-28-C9, literal (`ui/MG-ILE-56.md`): el porqué de «No grave» deshabilitado. */
const TEXTO_GRAVEDAD_NO_PUEDE_BAJAR = 'Ya se notificó a la autoridad: la gravedad no puede bajar';
/** El texto del servidor cuando el caso no tiene gravedad decidida (`_reclasificacion`, 422). */
const TEXTO_SIN_GRAVEDAD = 'Para reclasificar hace falta la gravedad actual del caso.';
/** La opción que ya es la gravedad del caso: el servidor tampoco la acepta (422). */
const TEXTO_GRAVEDAD_ACTUAL = 'Es la gravedad actual del caso.';
/** Las dos gravedades del radio, con el texto del glosario (`ui/MG-ILE-54.md`, «Reapertura»). */
const OPCIONES_DE_GRAVEDAD = [
  { value: 'grave', label: GRAVEDADES.grave, testid: 'reapertura-form-gravedad-grave' },
  { value: 'no_grave', label: GRAVEDADES.no_grave, testid: 'reapertura-form-gravedad-no-grave' },
] as const;

/**
 * El porqué de una gravedad que la reapertura no acepta (`ui/MG-ILE-56.md`, HU-28-C9). El servidor
 * dice CUÁLES acepta (`reclasificaciones_permitidas`); fuera de la gravedad actual, la única que
 * puede faltar es «No grave» de un grave ya notificado (`reclasificaciones_permitidas` del dominio).
 */
function porQueNoSePuede(value: string, caso: CasoDelExpediente): string | undefined {
  if (caso.reclasificaciones_permitidas.includes(value)) return undefined;
  if (value === caso.gravedad) return TEXTO_GRAVEDAD_ACTUAL;
  return TEXTO_GRAVEDAD_NO_PUEDE_BAJAR;
}
const ESTADO_CERRADO = 'cerrado';

/** «C-05» → `expediente-cierre-condicion-c-05` (testid de la spec, kebab-case). */
const testidDeCondicion = (control: string) => `expediente-cierre-condicion-${control.toLowerCase()}`;

/** El éxito que se anuncia tras cerrar o reabrir (estado «Éxito» de la spec). */
type Exito = { titulo: string; texto?: string | null; plazo?: string | null };

function Condicion({ c, onVerCompromisos }: { c: CondicionDelCierre; onVerCompromisos?: () => void }) {
  const Icono = c.cumplida ? CheckCircle2 : AlertTriangle;
  return (
    <li data-testid={testidDeCondicion(c.control)} data-cumplida={c.cumplida}
      className="flex flex-wrap items-start gap-2 text-body">
      <Icono size={14} aria-hidden="true"
        className={'shrink-0 mt-0.5 ' + (c.cumplida ? 'text-emerald-600' : 'text-amber-600')} />
      {/* Sin el código del control (C-07, C-08): el icono ya dice si está cumplida y el
          código no le dice nada al usuario (decisión del operador, 5-oct-2026). */}
      <span className="min-w-0 flex-1">{c.texto}</span>
      {c.control === 'C-08' && onVerCompromisos && (
        <Button size="sm" variant="ghost" icon={ListChecks} onClick={onVerCompromisos}
          data-testid="expediente-cierre-ver-compromisos">
          Ver compromisos
        </Button>
      )}
    </li>
  );
}

/**
 * HU-28 · el modal «Reabrir». Un solo motivo para reabrir y reclasificar (vacío 6 de la spec):
 * el contrato pide los dos campos (`motivo` y `motivo_de_reclasificacion`) y se manda el mismo
 * texto en ambos. Las gravedades que se ofrecen son las de `caso.reclasificaciones_permitidas`:
 * la que falta queda deshabilitada con su porqué (HU-28-C9), y si llega igual, el 409 del
 * servidor se pinta bajo el campo. Elegir «Grave» muestra `caso.texto_del_plazo_si_grave` antes
 * de confirmar (HU-28-C8).
 */
function ModalDeReapertura({ casoId, numero, caso, abierto, onCerrar, onReabierto }: {
  casoId: string; numero?: string | null; caso: CasoDelExpediente; abierto: boolean; onCerrar: () => void;
  onReabierto: (exito: Exito) => void;
}) {
  const [motivo, setMotivo] = useState('');
  const [tocado, setTocado] = useState(false);
  const [reclasificar, setReclasificar] = useState(false);
  const [gravedad, setGravedad] = useState<'grave' | 'no_grave' | ''>('');
  const [ocupado, setOcupado] = useState(false);
  const [falloDelMotivo, setFalloDelMotivo] = useState<string | null>(null);
  const [falloDeLaGravedad, setFalloDeLaGravedad] = useState<string | null>(null);
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const nombreDelGrupo = useId();

  useEffect(() => {
    if (abierto) return;
    setMotivo(''); setTocado(false); setReclasificar(false); setGravedad('');
    setFalloDelMotivo(null); setFalloDeLaGravedad(null); setFallo(null);
  }, [abierto]);

  const sinMotivo = !motivo.trim();
  const faltaLaGravedad = reclasificar && !gravedad;
  // Sin gravedad decidida no hay nada que reclasificar (el servidor respondería 422).
  const sinGravedad = !caso.gravedad;
  const avisoDelPlazo = reclasificar && gravedad === 'grave' ? caso.texto_del_plazo_si_grave : null;
  const errorDelMotivo = falloDelMotivo ?? (tocado && sinMotivo ? MOTIVO_OBLIGATORIO : null);

  const reabrir = async () => {
    setTocado(true);
    if (sinMotivo || faltaLaGravedad) return;
    setOcupado(true);
    setFallo(null); setFalloDelMotivo(null); setFalloDeLaGravedad(null);
    try {
      const r = await reabrirCaso(casoId, {
        motivo: motivo.trim(),
        gravedad_nueva: reclasificar && gravedad ? gravedad : null,
        motivo_de_reclasificacion: reclasificar && gravedad ? motivo.trim() : null,
      });
      onReabierto({ titulo: 'Caso reabierto: vuelve a seguimiento', texto: r.texto_de_reaperturas, plazo: r.advertencia_de_plazo });
    } catch (e) {
      const f = aFallo(e);
      // 409: la gravedad no puede bajar (C9); 422 con reclasificación: su motivo (C7). Va bajo
      // el campo que lo provocó, con el texto del servidor.
      if (f.estado === 409 && reclasificar) setFalloDeLaGravedad(f.message);
      else if (f.estado === 422 && reclasificar) setFalloDeLaGravedad(f.message);
      else if (f.estado === 422) setFalloDelMotivo(f.message);
      else setFallo(f);
    } finally {
      setOcupado(false);
    }
  };

  return (
    <Modal open={abierto} onClose={onCerrar} tone="warning" icon={RotateCcw} sucio={Boolean(motivo.trim())}
      eyebrow="Cierre del caso" title={numero ? `Reabrir el caso ${numero}` : 'Reabrir el caso'}
      data-testid="reapertura-form"
      footer={<>
        <Button variant="secondary" onClick={onCerrar} data-testid="reapertura-form-cancelar">Cancelar</Button>
        <Button icon={RotateCcw} onClick={() => { reabrir(); }} disabled={ocupado || sinMotivo || faltaLaGravedad}
          data-testid="reapertura-form-guardar">
          {ocupado ? 'Reabriendo…' : 'Reabrir'}
        </Button>
      </>}>
      {fallo && (
        <div className="mb-3">
          <Alert tone={fallo.estado === 403 ? 'warning' : 'danger'} title="No se reabrió el caso"
            detalleTecnico={fallo.detalleTecnico} data-testid="reapertura-error-envio">
            {fallo.message}
          </Alert>
        </div>
      )}
      <Field label="Motivo" required error={errorDelMotivo}
        hint="Queda en el historial del caso con quién reabrió y cuándo.">
        <Textarea rows={3} maxLength={2000} value={motivo} data-testid="reapertura-form-motivo"
          onChange={(e) => { setMotivo(e.target.value); setFalloDelMotivo(null); }}
          onBlur={() => setTocado(true)} />
      </Field>
      <div className="mt-2">
        <Checkbox checked={reclasificar} data-testid="reapertura-form-reclasificar" disabled={sinGravedad}
          hint={sinGravedad ? TEXTO_SIN_GRAVEDAD : undefined}
          onChange={() => { setReclasificar((r) => !r); setGravedad(''); setFalloDeLaGravedad(null); }}
          label="La información nueva cambia la gravedad" />
      </div>
      {reclasificar && (
        <Field label="Gravedad nueva" grupo required className="mt-3" data-testid="reapertura-form-gravedad"
          error={falloDeLaGravedad}>
          <div className="flex flex-wrap gap-2">
            {OPCIONES_DE_GRAVEDAD.map((g) => {
              const bloqueo = porQueNoSePuede(g.value, caso);
              return (
                <Radio key={g.value} name={nombreDelGrupo} value={g.value} label={g.label} checked={gravedad === g.value}
                  disabled={Boolean(bloqueo)} hint={bloqueo} data-testid={g.testid}
                  onChange={() => { setGravedad(g.value); setFalloDeLaGravedad(null); }} />
              );
            })}
          </div>
        </Field>
      )}
      {avisoDelPlazo && (
        // HU-28-C8: pasar a grave no reinicia el plazo; el texto lo calcula el servidor y se lee
        // ANTES de confirmar.
        <div className="mt-3">
          <Alert tone="warning" title="Plazo de notificación" data-testid="reapertura-aviso-plazo">{avisoDelPlazo}</Alert>
        </div>
      )}
    </Modal>
  );
}

export default function CierreDelCaso({ casoId, numeroDeCaso = null, caso = null, onCambio, onVerCompromisos }: {
  casoId: string;
  numeroDeCaso?: string | null;
  /** `caso.*` del expediente: de él salen «Reabrir» y lo que ofrece su modal. */
  caso?: CasoDelExpediente | null;
  /** Tras cerrar o reabrir: el expediente se refresca desde el servidor (§8.1 R3). */
  onCambio?: () => void;
  /** «Ver compromisos» de la condición C-08: Disposiciones y seguimiento de este caso. */
  onVerCompromisos?: () => void;
}) {
  const usuario = usuarioActual();
  // Multi-rol (HU-32b): basta con que la persona tenga UNO de los roles; `tieneRol` mira todos
  // los de la sesión, no solo el primero.
  const conAlguno = (roles: string[]) => roles.some((r) => tieneRol(usuario, r));
  const veElCierre = conAlguno(ROLES_QUE_VEN_EL_CIERRE);
  const cierra = conAlguno(ROLES_QUE_CIERRAN);
  // HU-28-C1 y C5: lo dice el servidor, ya resuelto para esta sesión y este caso.
  const reabre = Boolean(caso?.acciones_permitidas.includes(ACCION_REABRIR));
  const idDeLaLista = useId();

  const [estado, setEstado] = useState<EstadoDelCierre | null>(null);
  const [falloDeCarga, setFalloDeCarga] = useState<Fallo | null>(null);
  const [falloDeEnvio, setFalloDeEnvio] = useState<Fallo | null>(null);
  const [confirmando, setConfirmando] = useState(false);
  const [reabriendo, setReabriendo] = useState(false);
  const [observacion, setObservacion] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [exito, setExito] = useState<Exito | null>(null);

  const cargar = useCallback(async () => {
    try {
      setEstado(await estadoDelCierre(casoId));
      setFalloDeCarga(null);
    } catch (e) {
      setFalloDeCarga(aFallo(e));
    }
  }, [casoId]);

  useEffect(() => {
    if (!veElCierre) return undefined;
    let vivo = true;
    estadoDelCierre(casoId)
      .then((e) => { if (vivo) setEstado(e); })
      .catch((e) => { if (vivo) setFalloDeCarga(aFallo(e)); });
    return () => { vivo = false; };
  }, [casoId, veElCierre]);

  const cerrar = async () => {
    setFalloDeEnvio(null);
    setExito(null);
    setOcupado(true);
    try {
      // HU-27-C7: sin «cerrado por»; lo pone el servidor desde la sesión.
      setEstado(await cerrarCaso(casoId, { observacion: observacion.trim() || null }));
      setConfirmando(false);
      setObservacion('');
      setExito({ titulo: 'Caso cerrado', texto: 'Las invitaciones de especialistas caducaron. El expediente sigue disponible en solo lectura.' });
      onCambio?.();
    } catch (e) {
      const f = aFallo(e);
      setFalloDeEnvio(f);
      setConfirmando(false);
      // El 422 trae TODAS las condiciones pendientes: se refresca lo que se ve (C14).
      if (f.estado === 422 || f.estado === 409) cargar();
    } finally {
      setOcupado(false);
    }
  };

  const alReabrir = (resultado: Exito) => {
    setReabriendo(false);
    setFalloDeEnvio(null);
    setExito(resultado);
    cargar();
    onCambio?.();
  };

  if (!veElCierre) return null;
  if (falloDeCarga?.estado === 404 || falloDeCarga?.estado === 403) return null;

  const pendientesDelFallo = (falloDeEnvio?.cuerpo as CierreBloqueado | null)?.pendientes ?? [];
  // Las condiciones del Sprint 3 (`condiciones[]`); con un servidor anterior, las pendientes.
  const condiciones: CondicionDelCierre[] = estado?.condiciones?.length
    ? estado.condiciones
    : (estado?.condiciones_pendientes ?? []).map((c) => ({ control: c.control ?? c.condicion, cumplida: false, texto: c.detalle }));
  const estaCerrado = estado?.estado_seguimiento === ESTADO_CERRADO;

  return (
    <Card className="mt-4" title="Cierre del caso" icon={Lock}
      subtitle="Se cierra cuando todas las condiciones están cumplidas. Cerrar no borra nada: el expediente queda consultable.">
      {exito && (
        <div className="mb-3">
          <Alert tone="success" title={exito.titulo} data-testid="expediente-exito" onCerrar={() => setExito(null)}>
            {exito.texto}
          </Alert>
        </div>
      )}
      {exito?.plazo && (
        // HU-28-C8: pasar a grave no reinicia el plazo; el texto lo calcula el servidor.
        <div className="mb-3">
          <Alert tone="warning" title="Plazo de notificación" data-testid="reapertura-aviso-plazo">{exito.plazo}</Alert>
        </div>
      )}
      {falloDeEnvio && (
        <div className="mb-3">
          <Alert tone={falloDeEnvio.estado === 422 || falloDeEnvio.estado === 409 ? 'warning' : 'danger'}
            title={falloDeEnvio.estado === 422 || falloDeEnvio.estado === 409 ? 'Todavía no se puede cerrar' : 'No se pudo cerrar el caso'}
            detalleTecnico={falloDeEnvio.detalleTecnico} data-testid="expediente-error-envio"
            onCerrar={() => setFalloDeEnvio(null)}>
            {falloDeEnvio.message}
            {pendientesDelFallo.length > 0 && (
              <ul className="list-disc pl-4 mt-1">
                {pendientesDelFallo.map((c) => <li key={c.condicion}>{c.detalle}</li>)}
              </ul>
            )}
          </Alert>
        </div>
      )}
      {falloDeCarga && (
        <Alert tone="danger" title="No se pudo consultar el cierre" detalleTecnico={falloDeCarga.detalleTecnico}>
          {falloDeCarga.message}
        </Alert>
      )}
      {!estado && !falloDeCarga && <p className="text-body text-ink-soft">Consultando las condiciones…</p>}
      {estado && (
        <>
          {condiciones.length === 0
            ? <p className="text-body inline-flex items-center gap-1.5"><CheckCircle2 size={15} className="text-emerald-600" aria-hidden="true" /> Todas las condiciones están cumplidas.</p>
            : (
              <ul id={idDeLaLista} className="space-y-1.5" aria-label="Condiciones del cierre" data-testid="expediente-cierre-condiciones">
                {condiciones.map((c) => <Condicion key={c.control} c={c} onVerCompromisos={onVerCompromisos} />)}
              </ul>
            )}
          {((cierra && !estaCerrado) || reabre) && (
            <div className="mt-3 flex flex-wrap items-center gap-3">
              {cierra && !estaCerrado && (
                <Button icon={Lock} onClick={() => setConfirmando(true)} data-testid="expediente-cerrar"
                  aria-describedby={condiciones.length ? idDeLaLista : undefined}
                  disabled={!estado.se_puede_cerrar || ocupado}>
                  Cerrar caso
                </Button>
              )}
              {reabre && (
                <Button variant="secondary" icon={RotateCcw} onClick={() => setReabriendo(true)} data-testid="expediente-reabrir">
                  Reabrir
                </Button>
              )}
            </div>
          )}
        </>
      )}
      <Modal open={confirmando} onClose={() => setConfirmando(false)} tone="warning" icon={Lock}
        eyebrow="Cierre del caso" title={numeroDeCaso ? `¿Cerrar el caso ${numeroDeCaso}?` : '¿Cerrar el caso?'}
        footer={<>
          <Button variant="secondary" onClick={() => setConfirmando(false)} data-testid="expediente-cancelar-cerrar">Cancelar</Button>
          {/* Primario y no `danger`: cerrar no borra nada (el expediente queda consultable) y el
              rojo del catálogo con texto blanco no llega a 4,5:1 (axe, ola 3). */}
          <Button icon={Lock} onClick={() => { cerrar(); }} disabled={ocupado}
            data-testid="expediente-confirmar-cerrar">
            Cerrar caso
          </Button>
        </>}>
        <Alert tone="warning">Al cerrar, las invitaciones de especialistas caducan y el expediente se archiva.</Alert>
        <Field label="Observación" hint="Opcional. Queda en el historial del caso con quién cerró y cuándo.">
          <Textarea aria-label="Observación del cierre" rows={2} value={observacion} onChange={(e) => setObservacion(e.target.value)} />
        </Field>
      </Modal>
      {reabre && caso && (
        <ModalDeReapertura casoId={casoId} numero={numeroDeCaso} caso={caso} abierto={reabriendo}
          onCerrar={() => setReabriendo(false)} onReabierto={alReabrir} />
      )}
    </Card>
  );
}
